<template>
  <PrimaryModal @clickBackground="close()">
    <div
      class="problem-select-wrapper max-w-screen-lg box-border px-12 py-8 m-4 overflow-y-scroll notranslate"
      :class="{
        'custom-scrollbar': isLightMode(),
        'custom-scrollbar-dark': !isLightMode()
      }"
      style="user-select: none"
    >
      <div class="w-full grid grid-cols-3 gap-4 mt-2">
        <part-button
          :isSelect="
            problemSearchModalStatePresenter.modalState.value == 'COURSE' ||
            problemSearchModalStatePresenter.modalState.value == 'COURSE_PROBLEM'
          "
          :onClick="clickCourse"
          partType="COURSE"
        />
        <part-button
          :isSelect="
            problemSearchModalStatePresenter.modalState.value == 'CHALLENGE' ||
            problemSearchModalStatePresenter.modalState.value == 'CHALLENGE_PROBLEM'
          "
          :onClick="clickChallenge"
          partType="CHALLENGE"
        />
        <part-button
          :isSelect="problemSearchModalStatePresenter.modalState.value == 'BOOKMARK'"
          :onClick="clickBookmark"
          partType="BOOKMARK"
        />
      </div>
      <div class="mt-4 border bg-white border-primary-50 dark:bg-dark-500 dark:border-dark-400">
        <div>
          <h2
            class="text-xl font-bold pl-4 py-2 bg-primary-500 text-white dark:bg-dark-400 dark:border-dark-400 dark:text-dark-text"
          >
            {{ title }}
          </h2>
          <p class="p-2 pl-4">{{ subtitle }}</p>
        </div>
      </div>
      <div style="height: 50vh">
        <ProblemSelect
          :onClickBookmark="clickProblem"
          :onClickChallenge="clickChallenge"
          :onClickCourse="clickCourse"
          :onClickCourseCard="clickCourseCard"
          :onClickMiddleCl="clickMiddleCl"
          :onClickProblem="clickProblem"
        />
      </div>
    </div>
  </PrimaryModal>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref } from 'vue'

import ProblemSelect from './children//ProblemSelect.vue'
import PartButton from './children/PartButton.vue'

import PrimaryModal from '@/presentation/components/Modal/PrimaryModal.vue'
import { useArenaPresenter } from '@/presentation/composables/arena/useArenaPresenter'
import { useAuthStatePresenter } from '@/presentation/composables/auth/useAuthStatePresenter'
import { useBookmarkPresenter } from '@/presentation/composables/bookmark/useBookmarkPresenter'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useModalStatePresenter } from '@/presentation/composables/modal/useModalStatePresenter'
import { useChallengePresenter } from '@/presentation/composables/problem/useChallengePresenter'
import { useCoursePresenter } from '@/presentation/composables/problem/useCoursePresenter'
import { useProblemSearchModalStatePresenter } from '@/presentation/composables/problem/useProblemSearchModalStatePresenter'
import { useProblemStatePresenter } from '@/presentation/composables/problem/useProblemStatePresenter'

export default defineComponent({
  components: {
    PrimaryModal,
    ProblemSelect,
    PartButton
  },
  setup(props, ctx) {
    const coursePresenter = useCoursePresenter()
    const challengePresenter = useChallengePresenter()
    const bookmarkPresenter = useBookmarkPresenter()
    const problemSearchModalStatePresenter = useProblemSearchModalStatePresenter()
    const { setProblemId } = useProblemStatePresenter()
    const modalStatePresenter = useModalStatePresenter()
    const { user, isLogin } = useAuthStatePresenter()
    const { displayLanguage, isLightMode } = useConfigStatePresenter()

    const title = ref('Select Part')
    const subtitle = ref('')

    const close = () => {
      modalStatePresenter.setIsShowProblemSearchModal(false)
    }

    const hotKeys = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        close()
      }
    }

    onUnmounted(() => {
      document.removeEventListener('keydown', hotKeys)
    })

    onMounted(() => {
      document.addEventListener('keydown', hotKeys)
      switch (problemSearchModalStatePresenter.modalState.value) {
        case 'COURSE':
        default:
          clickCourse()
          break
        case 'CHALLENGE':
          clickChallenge()
          break
        case 'BOOKMARK':
          clickBookmark()
          break
      }
    })

    // displayLang しゅうせい
    const clickCourse = async () => {
      problemSearchModalStatePresenter.setModalState('COURSE')
      title.value = displayLanguage.value === 'Japanese' ? 'コースリスト' : 'Courses'
      subtitle.value =
        displayLanguage.value === 'Japanese' ? 'プログラムの基礎を学ぼう' : 'Learn the basics of the program'
      await coursePresenter.getCourseList(displayLanguage.value)
    }

    const clickChallenge = async () => {
      problemSearchModalStatePresenter.setModalState('CHALLENGE')
      title.value = displayLanguage.value === 'Japanese' ? 'チャレンジリスト' : 'Challenge'
      subtitle.value =
        displayLanguage.value === 'Japanese' ? 'コンテストの過去問に挑戦' : 'Challenge the past problem of the contest'
      await challengePresenter.getChallengeList(displayLanguage.value)
    }

    const clickBookmark = async () => {
      problemSearchModalStatePresenter.setModalState('BOOKMARK')
      title.value = displayLanguage.value === 'Japanese' ? 'ブックマークリスト' : 'Bookmark'

      if (isLogin.value) {
        await bookmarkPresenter.getBookmark(user.value.id)
        subtitle.value =
          displayLanguage.value === 'Japanese' ? 'ブックマークに追加した問題に挑戦' : 'Challenge the bookmarked problem'
      } else {
        subtitle.value = displayLanguage.value === 'Japanese' ? 'ログインしてください' : 'Please login'
      }
    }

    const clickCourseCard = async (id: number) => {
      // 毎回最新情報を取得する必要はない
      await coursePresenter.getCourseById(displayLanguage.value, id)
      problemSearchModalStatePresenter.setSelectedCourse(coursePresenter.courseList.value[id])
      if ('id' in problemSearchModalStatePresenter.selectedCourse.value) {
        title.value = problemSearchModalStatePresenter.selectedCourse.value.name
        subtitle.value = problemSearchModalStatePresenter.selectedCourse.value.description
      }
      problemSearchModalStatePresenter.setModalState('COURSE_PROBLEM')
    }

    const clickMiddleCl = async (lId: string, mId: string) => {
      // 毎回最新情報を取得する必要はない
      await challengePresenter.getChallengeById(displayLanguage.value, lId, mId)
      problemSearchModalStatePresenter.setSelectedContestList(challengePresenter.contestList.value[lId][mId])
      if (problemSearchModalStatePresenter.selectedContestList.value.length > 0) {
        title.value = challengePresenter.largeClassificationList.value[lId].title
        subtitle.value = ''
      }
      problemSearchModalStatePresenter.setModalState('CHALLENGE_PROBLEM')
    }

    const { setArenaProblem } = useArenaPresenter()

    const clickProblem = (id: string) => {
      setArenaProblem('', '', '', -1)
      setProblemId(id)
    }

    return {
      title,
      subtitle,
      isLightMode,
      close,
      clickCourse,
      clickChallenge,
      clickBookmark,
      clickCourseCard,
      clickProblem,
      clickMiddleCl,
      problemSearchModalStatePresenter
    }
  }
})
</script>

<style lang="postcss" scoped>
.problem-select-wrapper {
  width: 80vw;
  height: 80vh;
}
</style>
